import { DateTime, Duration } from 'luxon'
import { Alert } from '../../../schema'

function formatCSVField(data: string): string {
  if (!/[,"\r\n]/.test(data)) return data

  return `"${data.replace(/"/g, '""')}"`
}

export type useAlertCSVOpts = {
  alerts: Alert[]
  urlPrefix: string
}

export function useAlertCSV({ urlPrefix, alerts }: useAlertCSVOpts): string {
  let data = ''
  const zoneAbbr = DateTime.local().toFormat('ZZZZ Z')
  const cols = [
    `Alert ID`,
    `Service Name`,
    `Summary`,
    `Status`,
    `Escalation Count`,
    `Created At (${zoneAbbr})`,
    `Closed At (${zoneAbbr})`,
    `Time to Ack`,
    `Time to Close`,
    `Resolved Time`,
    `Noise Reason`,
    `Details`,
  ]
  data += cols.map(formatCSVField).join(',') + '\r\n'

  const rows = alerts.map(
    (a) =>
      [
        `=HYPERLINK("${urlPrefix}/alerts/${a.alertID}","${a.alertID}")`,
        `=HYPERLINK("${urlPrefix}/services/${a.service?.id}","${a.service?.name || ''}")`,
        a.summary,
        (() => {
          const status = a.status.replace('Status', '')
          const event = a.recentEvents?.nodes?.[0]
          const subject = event?.subject

          if (a.status === 'StatusUnacknowledged') {
             if (event?.message?.includes('Escalated')) {
                 if (subject?.name) {
                    if (subject.type === 'User') {
                        return `=HYPERLINK("${urlPrefix}/users/${subject.id}", "Escalated by ${subject.name}")`
                    }
                    return `Escalated by ${subject.name}`
                 }
                 return 'Auto Escalated'
             }
             return 'Unacknowledged'
          }
          
          let action = ''
          if (a.status === 'StatusAcknowledged') action = 'Acknowledged by'
          if (a.status === 'StatusClosed') action = 'Closed by'

          if (!subject) return status

          if (subject.type === 'User') {
              return `=HYPERLINK("${urlPrefix}/users/${subject.id}", "${action} ${subject.name}")`
          }
          return `${action} ${subject.name}`
        })(),
        (a.metrics?.escalationCount || 0).toString(),
        DateTime.fromISO(a.createdAt).toLocal().toSQL({
          includeOffset: false,
        }),
        a.metrics?.closedAt
          ? DateTime.fromISO(a.metrics.closedAt as string)
              .toLocal()
              .toSQL({
                includeOffset: false,
              }) || ''
          : '',
        a.metrics?.timeToAck
          ? Duration.fromISO(a.metrics.timeToAck as string).toFormat('hh:mm:ss')
          : '',
        a.metrics?.timeToClose
          ? Duration.fromISO(a.metrics.timeToClose as string).toFormat(
              'hh:mm:ss',
            )
          : '',
        (a.metrics?.timeToClose && a.metrics?.timeToAck)
          ? Duration.fromISO(a.metrics.timeToClose as string).minus(Duration.fromISO(a.metrics.timeToAck as string)).toFormat('hh:mm:ss')
          : '',
        a.noiseReason || '',
        a.details,
      ]
        .map((s) => formatCSVField(s || ''))
        .join(',') + '\r\n',
  )

  return data + rows.join('')
}
